/** @jsx jsx */
import React from "react";
import type { Property } from "csstype";
import { jsx, keyframes } from "@emotion/core";
import { colors } from "../colors";
import { useSpaceKitProvider } from "../SpaceKitProvider";

type Size = "large" | "medium" | "small" | "xsmall" | "2xsmall";
type Theme = "light" | "dark" | "grayscale";
interface Props {
  /**
   * Class name that will be applied to the svg
   */
  className?: string;

  /**
   * Theme for the spinner
   * @default "light"
   */
  theme?: Theme;

  /**
   * Size of the spinner
   * @default "medium"
   */
  size?: Size;
}

/**
 * @deprecated use Orbit's `Spinner` instead.
 * import { Spinner } from '@apollo/orbit'
 */
export const LoadingSpinner = ({
  theme: propTheme,
  size = "medium",
  className,
  ...props
}: Props & { ref?: React.Ref<SVGSVGElement> }) => {
  const { theme: providerTheme } = useSpaceKitProvider();
  const theme = propTheme || providerTheme;

  /* Length of animation */
  const DURATION = 1000;

  const SPIN = keyframes`
    0% { transform: rotate(0) }
    100% { transform: rotate(360deg) }
  `;

  const SIZE_MAP: Record<Size, number> = {
    large: 90,
    medium: 64,
    small: 48,
    xsmall: 32,
    "2xsmall": 16,
  };

  const THEME_MAP: Record<
    Theme,
    {
      orbitColor: Property.Color;
      asteroidColor: Property.Color;
    }
  > = {
    light: {
      orbitColor: colors.silver.darker,
      asteroidColor: colors.blue.base,
    },
    dark: {
      orbitColor: colors.midnight.lighter,
      asteroidColor: colors.white,
    },
    grayscale: {
      orbitColor: colors.silver.darker,
      asteroidColor: colors.grey.light,
    },
  };

  const { disableAnimations } = useSpaceKitProvider();

  const { orbitColor, asteroidColor } = THEME_MAP[theme];

  const pixelSize = SIZE_MAP[size];

  const mountTime = React.useRef(Date.now());
  const mountDelay = disableAnimations ? 0 : -(mountTime.current % DURATION);

  return (
    <svg
      className={className}
      role="progressbar"
      viewBox="0 0 100 100"
      css={{
        width: pixelSize,
        height: pixelSize,
      }}
      {...props}
    >
      <circle
        strokeWidth="8"
        stroke={orbitColor}
        strokeOpacity={1}
        fill="transparent"
        r="41"
        cx="50"
        cy="50"
      />
      <g transform="translate(50 50)">
        <circle
          css={{
            animation: `${SPIN} ${DURATION}ms linear infinite`,
            willChange: "transform",
            animationDelay: `${mountDelay}ms`,
          }}
          fill={asteroidColor}
          r="10"
          cx="40"
          cy="0"
        />
      </g>
    </svg>
  );
};
